
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js';

export interface FunnelStage {
  key: string;
  label: string;
}

/**
 * What each stage bar is split into: the two sources, the teams, or the top
 * commercial accounts. The first segment is drawn solid, the rest as lighter
 * tints of the stage colour, in order.
 */
export interface FunnelSegment {
  key: string;
  label: string;
}

/** One x-axis bucket: a period plus, per stage and segment, a volume and a count. */
export interface FunnelPoint {
  period: string;
  label: string;
  volume: Record<string, Record<string, number>>;
  count: Record<string, Record<string, number>>;
}

// Funnel order, cool → warm-green: the eye reads left-to-right as "money
// getting more real". Keyed by stage so the table and the legend agree.
export const STAGE_COLORS: Record<string, string> = {
  created: '#90a4ae',
  sent: '#42a5f5',
  accepted_quote: '#26a69a',
  accepted_direct: '#00acc1',
  delivered: '#66bb6a',
  invoiced: '#2e7d32',
};

// Segments of one stage share its hue and step down in opacity, first segment
// solid. Same stage, same stack, so the bar height is the combined figure and
// the split stays visible. The floor keeps the last of many tints readable.
const MIN_ALPHA = 0.2;

function segmentAlpha(index: number, count: number): number {
  if (count <= 1) return 1;
  return 1 - (index * (1 - MIN_ALPHA)) / (count - 1);
}

// Every amount on the funnel is shown in thousands of euros, as the Metabase
// questions it replaces did (`sum(volume)/1000`). One decimal keeps a EUR 189
// quote from reading as nothing. Shared with the view so table and chart agree.
const kEur = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function formatKEur(value: number): string {
  return `${kEur.format((Number(value) || 0) / 1000)} k€`;
}

function esc(value: string): string {
  return String(value).replace(
    /[&<>"]/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string),
  );
}

function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

@Component
export default class RevenueFunnelChart extends Vue {
  @Prop({ required: true }) points!: FunnelPoint[];
  @Prop({ required: true }) stages!: FunnelStage[];
  @Prop({ required: true }) segments!: FunnelSegment[];

  private chart: Chart | null = null;

  mounted() {
    this.draw();
  }

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }

  private tooltipEl: HTMLDivElement | null = null;

  private ensureTooltip(): HTMLDivElement {
    if (!this.tooltipEl) {
      const el = document.createElement('div');
      el.className = 'revenue-funnel-tooltip';
      el.style.opacity = '0';
      document.body.appendChild(el);
      this.tooltipEl = el;
    }
    return this.tooltipEl;
  }

  /** One period as a table: rows are the segments, columns the visible stages. */
  private renderTooltip(model: any) {
    const el = this.ensureTooltip();
    const canvas = this.$refs.canvas as HTMLCanvasElement | undefined;
    const chart = this.chart;
    if (
      !model ||
      model.opacity === 0 ||
      !model.dataPoints?.length ||
      !canvas ||
      !chart
    ) {
      el.style.opacity = '0';
      return;
    }
    const point = this.points[model.dataPoints[0].index];
    if (!point) {
      el.style.opacity = '0';
      return;
    }
    const segmentCount = this.segments.length;
    const stages = this.stages.filter((_s, i) =>
      chart.isDatasetVisible(i * segmentCount),
    );

    const head = stages
      .map(
        (s) =>
          `<th style="color:${STAGE_COLORS[s.key] || '#ccc'}">${esc(
            s.label,
          )}</th>`,
      )
      .join('');
    const body = this.segments
      .map((seg, i) => {
        const cells = stages
          .map((s) => {
            const v = point.volume[s.key]?.[seg.key] || 0;
            const c = point.count[s.key]?.[seg.key] || 0;
            return v || c
              ? `<td>${formatKEur(v)}<small> / ${c}</small></td>`
              : '<td class="muted">–</td>';
          })
          .join('');
        const swatch = `<i style="background:${withAlpha(
          '#ffffff',
          segmentAlpha(i, segmentCount),
        )}"></i>`;
        return `<tr><th>${swatch}${esc(seg.label)}</th>${cells}</tr>`;
      })
      .join('');
    const foot = stages
      .map((s) => {
        const v = Object.values(point.volume[s.key] || {}).reduce(
          (a, b) => a + b,
          0,
        );
        const c = Object.values(point.count[s.key] || {}).reduce(
          (a, b) => a + b,
          0,
        );
        return `<td>${formatKEur(v)}<small> / ${c}</small></td>`;
      })
      .join('');
    el.innerHTML =
      `<div class="title">${esc(point.label)}</div>` +
      `<table><thead><tr><th></th>${head}</tr></thead>` +
      `<tbody>${body}</tbody>` +
      (segmentCount > 1 ? `<tfoot><tr><th>Σ</th>${foot}</tr></tfoot>` : '') +
      `</table>`;

    // Next to the caret, but never outside the viewport: flip to the left of
    // the cursor when there is no room on the right, and slide vertically
    // rather than being cut at the bottom of the window.
    const rect = canvas.getBoundingClientRect();
    const x = rect.left + model.caretX;
    const y = rect.top + model.caretY;
    const margin = 12;
    el.style.opacity = '1';
    const { offsetWidth: w, offsetHeight: h } = el;
    let left = x + margin;
    if (left + w > window.innerWidth - margin) left = x - margin - w;
    left = Math.max(margin, left);
    let top = y - h / 2;
    top = Math.min(top, window.innerHeight - h - margin);
    top = Math.max(margin, top);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  @Watch('points')
  @Watch('stages')
  @Watch('segments')
  onDataChange() {
    this.draw();
  }

  private get datasets(): Chart.ChartDataSets[] {
    const out: Chart.ChartDataSets[] = [];
    for (const stage of this.stages) {
      this.segments.forEach((segment, i) => {
        out.push({
          label: `${stage.label} · ${segment.label}`,
          stack: stage.key,
          backgroundColor: withAlpha(
            STAGE_COLORS[stage.key] || '#90a4ae',
            segmentAlpha(i, this.segments.length),
          ),
          borderColor: STAGE_COLORS[stage.key] || '#90a4ae',
          borderWidth: i ? 1 : 0,
          data: this.points.map((p) => p.volume[stage.key]?.[segment.key] || 0),
          // Carried for the tooltip; chart.js leaves unknown keys alone.
          ...({
            stageKey: stage.key,
            segmentKey: segment.key,
            first: i === 0,
          } as any),
        });
      });
    }
    return out;
  }

  private draw() {
    if (!this.$refs.canvas) {
      return;
    }
    const ctx = (this.$refs.canvas as HTMLCanvasElement).getContext('2d');
    if (!ctx) {
      return;
    }

    const data = {
      labels: this.points.map((p) => p.label),
      datasets: this.datasets,
    };

    if (this.chart) {
      this.chart.data.labels = data.labels;
      this.chart.data.datasets = data.datasets;
      this.chart.update();
      return;
    }

    const stageLabel = (key: string) =>
      this.stages.find((s) => s.key === key)?.label || key;

    this.chart = new Chart(ctx, {
      type: 'bar',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'top',
          labels: {
            // One legend entry per stage. The tints within a stage are
            // explained once in the caption instead of multiplying the legend.
            filter: (item, chartData) => {
              const ds = chartData.datasets?.[item.datasetIndex ?? 0] as any;
              return !!ds?.first;
            },
            generateLabels: (chart) => {
              const ds = (chart.data.datasets || []) as any[];
              return ds
                .map((d, i) => ({ d, i }))
                .filter(({ d }) => d.first)
                .map(({ d, i }) => ({
                  text: stageLabel(d.stageKey),
                  fillStyle: STAGE_COLORS[d.stageKey] || '#90a4ae',
                  strokeStyle: STAGE_COLORS[d.stageKey] || '#90a4ae',
                  // Chart.js strikes the entry through when hidden; it has
                  // to be read from the dataset meta, or the toggle below
                  // leaves no visible trace in the legend.
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                }));
            },
          },
          // Clicking a stage toggles every segment of that stage.
          onClick: (_evt, item) => {
            const chart = this.chart;
            if (!chart) return;
            const clicked = (chart.data.datasets || [])[
              item.datasetIndex ?? 0
            ] as any;
            (chart.data.datasets || []).forEach((d: any, i) => {
              if (d.stageKey === clicked?.stageKey) {
                const meta = chart.getDatasetMeta(i);
                // chart.js 2 treats anything but `true` as visible; the
                // typings only allow boolean | undefined here.
                meta.hidden = meta.hidden ? undefined : true;
              }
            });
            chart.update();
            // The tooltip was rendered for the old visibility; the next
            // mouse move redraws it, until then it must not show a stage
            // that is no longer there.
            if (this.tooltipEl) this.tooltipEl.style.opacity = '0';
          },
        },
        // The built-in tooltip is painted onto the canvas and clipped by it;
        // with eight segments per stage it is taller than the chart. An HTML
        // tooltip on the body can be as tall as it needs and is clamped to
        // the viewport instead, and it can be a segment × stage table rather
        // than a forty-line list.
        tooltips: {
          enabled: false,
          mode: 'index',
          intersect: false,
          custom: (model) => this.renderTooltip(model),
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                callback: (value) => formatKEur(Number(value)),
              },
            },
          ],
        },
      },
    });
  }
}
